"use client";

import Cookies from "js-cookie";
import {
	CONSENT_COOKIE_MAX_AGE_DAYS,
	CONSENT_COOKIE_NAME,
	CONSENT_LOCALSTORAGE_KEY,
	type ConsentRecord,
} from "./constants";
import {
	buildDecidedConsentRecord,
	parseConsentCookieValue,
	serializeConsentRecord,
} from "./record";

/** Legacy cookie name before canonical `sq_consent`; remove if still present client-side. */
const LEGACY_CONSENT_COOKIE_NAME = "consent";

function consentCookieAttributes(): Cookies.CookieAttributes {
	return {
		expires: CONSENT_COOKIE_MAX_AGE_DAYS,
		path: "/",
		sameSite: "lax",
		secure:
			typeof window !== "undefined" &&
			window.location.protocol === "https:",
	};
}

export function readConsentLocalStorage(): ConsentRecord | null {
	if (typeof window === "undefined") {
		return null;
	}
	try {
		const raw = window.localStorage.getItem(CONSENT_LOCALSTORAGE_KEY);
		return parseConsentCookieValue(raw ?? undefined);
	} catch {
		return null;
	}
}

export function writeConsentLocalStorage(record: ConsentRecord): void {
	if (typeof window === "undefined") {
		return;
	}
	try {
		window.localStorage.setItem(
			CONSENT_LOCALSTORAGE_KEY,
			serializeConsentRecord(record),
		);
	} catch {
		// Storage full / disabled — cookie remains canonical when writable.
	}
}

export function writeConsentCookie(record: ConsentRecord): void {
	Cookies.set(CONSENT_COOKIE_NAME, serializeConsentRecord(record), {
		...consentCookieAttributes(),
	});
}

export function removeLegacyConsentCookie(): void {
	if (typeof window === "undefined") {
		return;
	}
	try {
		Cookies.remove(LEGACY_CONSENT_COOKIE_NAME, { path: "/" });
	} catch {
		// Ignore quota / storage disabled.
	}
}

export function persistConsentRecord(record: ConsentRecord): void {
	removeLegacyConsentCookie();
	writeConsentCookie(record);
	writeConsentLocalStorage(record);
}

/** Prefer mirror only when cookie parse fails client-side (edge hydration mismatch). */
export function reconcileConsentFromStorage(): ConsentRecord | null {
	const fromCookie = parseConsentCookieValue(
		Cookies.get(CONSENT_COOKIE_NAME),
	);
	if (fromCookie) {
		return fromCookie;
	}
	return readConsentLocalStorage();
}

export function detectDoNotTrack(): boolean {
	if (typeof navigator === "undefined") {
		return false;
	}
	const dnt =
		navigator.doNotTrack ??
		(
			navigator as Navigator & {
				msDoNotTrack?: string | undefined;
			}
		).msDoNotTrack;
	return dnt === "1";
}

export function detectGlobalPrivacyControl(): boolean {
	if (typeof navigator === "undefined") {
		return false;
	}
	return (
		(
			navigator as Navigator & {
				globalPrivacyControl?: boolean | undefined;
			}
		).globalPrivacyControl === true
	);
}

export function detectPrivacyRejectionSignal(): boolean {
	return detectDoNotTrack() || detectGlobalPrivacyControl();
}

export function persistDntRejectedConsent(): ConsentRecord {
	const record = buildDecidedConsentRecord({
		preferences: false,
		analytics: false,
		marketing: false,
	});
	persistConsentRecord(record);
	return record;
}

export function persistPrivacyRejectedConsent(): ConsentRecord {
	return persistDntRejectedConsent();
}
